import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, brl, fileUrl, maskDoc, printAgent, rows } from "@/lib/api";
import {
  CheckCircle2, FileText, MessageCircle, PauseCircle, Plus, Printer,
  Receipt, ShoppingCart, MessageSquareQuote, Trash2, X,
} from "lucide-react";
import { ConfirmDialog } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import Autocomplete from "@/components/panel/Autocomplete";
import { usePanelLink } from "@/components/panel/screens";
import {
  Badge, Button, DataTable, Field, FilterSheet, Input, PageHeader, SearchInput, Select,
} from "@/components/ui-kit";
import {
  CancelSaleDialog, ChangeSellerDialog, ChangePaymentDialog, ChangeCustomerDialog,
  EmitInvoiceDialog, SaleDetail, METHOD_LABEL, dt,
} from "./sales/SaleDialogs";
import { ConvertDialog, NewQuoteModal, QuoteDetail } from "./sales/QuoteDialogs";
import HeldDetail from "./sales/HeldDetail";

/**
 * Vendas & Orçamentos — o movimento do balcão numa lista só.
 *
 * Eram duas telas (Vendas e Orçamentos) e um terceiro lugar escondido dentro do
 * PDV (as vendas em aberto). A mesma pergunta — "o que aconteceu no balcão?" —
 * exigia abrir três lugares e cruzar de cabeça.
 *
 * A distinção entre os três é a PRIMEIRA coluna, e não uma aba: aba é a mesma
 * troca de tela com outro nome, e devolveria o problema que a fusão veio
 * resolver. Tudo chega junto, em ordem de criação, e o filtro de tipo existe
 * como recorte opcional — a tela ABRE com o movimento inteiro.
 */

/* A bandeira da linha. O tom segue a regra do sistema: emerald = dinheiro que
   entrou, brand = proposta em aberto (informação), amber = pendura que ainda
   ocupa o balcão, vermelho = desfeito. */
const KIND_META = {
  venda: { label: "Venda concluída", tone: "success", icon: Receipt },
  orcamento: { label: "Orçamento", tone: "info", icon: MessageSquareQuote },
  aberta: { label: "Venda em aberto", tone: "warning", icon: PauseCircle },
};

const QUOTE_STATUS = { enviado: "Enviado", pago: "Pago", convertido: "Convertido em venda" };

/** O que a coluna de situação mostra, que muda com o tipo da linha. */
function Situacao({ row }) {
  if (row.kind === "venda") {
    return row.status === "cancelada"
      ? <Badge tone="danger" dot>Cancelada</Badge>
      : <Badge>{METHOD_LABEL[row.payment_method] || row.payment_method}</Badge>;
  }
  if (row.kind === "orcamento") {
    const tone = row.status === "pago" ? "success" : row.status === "convertido" ? "info" : "warning";

    return <Badge tone={tone}>{QUOTE_STATUS[row.status] || row.status}</Badge>;
  }

  return <Badge tone="warning">Aguardando o cliente</Badge>;
}

function Tile({ label, value, accent, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1 font-mono text-lg font-semibold ${accent || "text-slate-900"}`}>{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function SalesLedger() {
  const panelLink = usePanelLink();
  const [sellerId, setSellerId] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [kind, setKind] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [sellers, setSellers] = useState([]);
  const [busy, setBusy] = useState(false);

  // diálogos
  const [detail, setDetail] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [changingSeller, setChangingSeller] = useState(null);
  const [changingPayment, setChangingPayment] = useState(null);
  const [changingCustomer, setChangingCustomer] = useState(null);
  const [emitting, setEmitting] = useState(null);
  const [converting, setConverting] = useState(null);
  const [payingQuote, setPayingQuote] = useState(null);
  const [discarding, setDiscarding] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  // Lista curta e fechada: cabe numa carga só (inclui desativados, que ainda
  // aparecem no histórico).
  useEffect(() => {
    api.get("/admin/sellers", { params: { situacao: "todos", per_page: 100 } })
      .then((r) => setSellers(rows(r))).catch(() => {});
  }, []);

  const {
    items, meta, total, page, lastPage, setPage, search, setSearch, sort, setSort, isLoading, isError, refetch,
  } = usePaginatedList("/admin/sales-ledger", {
    seller_id: sellerId || undefined,
    kind: kind || undefined,
    de: de || undefined,
    ate: ate || undefined,
  });

  const totals = meta?.totals;
  const hasFilters = !!(search || sellerId || kind || de || ate);
  const clearFilters = () => { setSearch(""); setSellerId(""); setSellerName(""); setKind(""); setDe(""); setAte(""); };

  const markPaid = async (q) => {
    setBusy(true);
    try { await api.post(`/admin/quotes/${q.id}/mark-paid`); toast.success("Marcado como pago"); refetch(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); setPayingQuote(null); }
  };

  const printQuote = async (q) => {
    setBusy(true);
    try {
      await printAgent.printApiPdf(`/admin/quotes/${q.id}/pdf`);
      toast.success(`Orçamento ${q.number} enviado para a impressora`);
    } catch {
      toast.error("Agente de impressão não encontrado nesta máquina. Use o botão de PDF para imprimir pelo navegador.");
    } finally { setBusy(false); }
  };

  const discardHeld = async (h) => {
    try {
      await api.delete(`/admin/sales-ledger/held/${h.id}`);
      toast.success("Venda em aberto descartada");
      refetch();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDiscarding(null); }
  };

  // Mesmos controles para a linha (desktop) e para a folha (mobile).
  const filtros = (
    <>
      {/* Recorte por tipo — NÃO é aba: o padrão é "tudo junto", e ele volta a
          isso ao limpar. Uma aba obrigaria a escolher um dos três antes de ver
          qualquer coisa. */}
      <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="ledger-kind-filter"
        aria-label="Filtrar por tipo de registro" className="w-auto px-2.5 text-slate-600">
        <option value="">Tudo (vendas, orçamentos e em aberto)</option>
        <option value="venda">Só vendas concluídas</option>
        <option value="orcamento">Só orçamentos</option>
        <option value="aberta">Só vendas em aberto</option>
      </Select>
      <Autocomplete
        value={sellerId}
        onChange={(v, l) => { setSellerId(v); setSellerName(l); }}
        options={sellers.map((s) => ({ value: s.id, label: s.active ? s.name : `${s.name} (desativado)` }))}
        selectedLabel={sellerName}
        ariaLabel="Filtrar por vendedor"
        placeholder="Todos os vendedores" testId="ledger-seller-filter" width="w-52" />
      <Field label="De" labelTone="plain" className="flex items-center gap-1.5">
        <Input type="date" value={de} onChange={(e) => setDe(e.target.value)} data-testid="ledger-from" className="w-auto px-2.5" />
      </Field>
      <Field label="até" labelTone="plain" className="flex items-center gap-1.5">
        <Input type="date" value={ate} onChange={(e) => setAte(e.target.value)} data-testid="ledger-to" className="w-auto px-2.5" />
      </Field>
      {hasFilters && (
        <Button variant="link" size="sm" icon={X} onClick={clearFilters} data-testid="ledger-clear-filters">Limpar</Button>
      )}
    </>
  );
  const filtrosAtivos = [sellerId, kind, de, ate].filter(Boolean).length;

  /** Abre o detalhe certo para o tipo da linha. */
  const openDetail = (row) => setDetail(row);

  return (
    <div className="space-y-5" data-testid="admin-sales-ledger-page">
      <PageHeader
        eyebrow="Balcão"
        title="Vendas & Orçamentos"
        icon={Receipt}
        description="Tudo o que passou pelo balcão, na ordem em que aconteceu: venda concluída, orçamento enviado e venda em aberto esperando o cliente. Pedidos da loja online ficam em Pedidos."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Nº, cliente, CPF ou telefone" data-testid="ledger-search"
              aria-label="Buscar por número, cliente, CPF ou telefone" width="w-60" />
            <Button icon={Plus} onClick={() => setShowQuoteForm(true)} data-testid="ledger-new-quote-btn">Novo orçamento</Button>
          </>
        )}
        filters={filtros}
        mobileFilters={<FilterSheet count={filtrosAtivos} onClear={clearFilters}>{filtros}</FilterSheet>}
      />

      {/* Cada tipo soma no próprio balde. Um "total geral" que juntasse os três
          seria um número sem significado: orçamento não é dinheiro que entrou, e
          venda em aberto nem sequer foi decidida. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ledger-totals">
        <Tile label="Vendas" value={totals?.vendas ?? 0}
          hint={totals?.canceladas ? `${totals.canceladas} cancelada(s)` : null} />
        <Tile label="Faturado" value={brl(totals?.vendas_total ?? 0)} accent="text-brand-700" />
        <Tile label="Em orçamento" value={brl(totals?.orcamentos_total ?? 0)}
          hint={`${totals?.orcamentos ?? 0} proposta(s)`} />
        <Tile label="Em aberto" value={brl(totals?.abertas_total ?? 0)} accent="text-amber-700"
          hint={`${totals?.abertas ?? 0} carrinho(s) parado(s)`} />
      </div>

      <DataTable
        caption="Vendas, orçamentos e vendas em aberto do balcão"
        rowTestId="ledger-row"
        rows={items}
        sort={sort}
        onSortChange={setSort}
        rowKey={(r) => `${r.kind}:${r.id}`}
        loading={isLoading}
        error={isError}
        onRetry={refetch}
        onRowClick={openDetail}
        empty={{
          icon: Receipt,
          title: hasFilters ? "Nada com esses filtros" : "Nenhum movimento no balcão ainda",
          desc: hasFilters
            ? "Ajuste o período ou limpe os filtros."
            : "Vendas, orçamentos e carrinhos guardados no PDV aparecem aqui assim que acontecerem.",
          action: hasFilters
            ? <Button variant="secondary" onClick={clearFilters}>Limpar filtros</Button>
            : <Button icon={Plus} onClick={() => setShowQuoteForm(true)}>Criar orçamento</Button>,
          testId: "ledger-empty",
        }}
        columns={[
          {
            /* A bandeira é a PRIMEIRA coluna: é o que separa os três tipos numa
               lista única, e precisa estar onde o olho já passa ao percorrer. */
            key: "tipo", header: "Tipo", width: "11rem", sortable: true,
            cell: (r) => {
              const m = KIND_META[r.kind];
              const Icone = m.icon;

              return (
                <Badge tone={m.tone} className="whitespace-nowrap">
                  <Icone size={12} aria-hidden /> {m.label}
                </Badge>
              );
            },
          },
          {
            key: "registro", header: "Nº / Identificação", sortable: true,
            cell: (r) => (
              <>
                <p className={`font-mono font-semibold ${r.status === "cancelada" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                  {/* Venda em aberto não tem número: ela ainda não é documento
                      nenhum. O rótulo que o vendedor deu ("Moça da cadeira
                      azul") é o que identifica o carrinho. */}
                  {r.number || <span className="font-sans italic text-slate-500">{r.label || "sem rótulo"}</span>}
                </p>
                <p className="text-xs text-slate-400">{dt(r.created_at)}</p>
              </>
            ),
          },
          {
            key: "cliente", header: "Cliente", sortable: true,
            cell: (r) => (
              <>
                <p className="text-slate-900">{r.customer_name || "—"}</p>
                {r.contato && (
                  <p className="text-xs text-slate-400 font-mono">
                    {r.kind === "orcamento" ? r.contato : maskDoc(r.contato)}
                  </p>
                )}
              </>
            ),
          },
          { key: "vendedor", header: "Vendedor", className: "text-slate-600", cell: (r) => r.seller_name || "—" },
          { key: "situacao", header: "Situação", sortable: true, cell: (r) => <Situacao row={r} /> },
          {
            key: "itens", header: "Itens", align: "center", mono: true, className: "text-slate-500",
            cell: (r) => r.qty ?? 0,
          },
          {
            key: "total", header: "Total", align: "right", sortable: true, sortDir: "desc",
            cell: (r) => (
              <>
                <span className={`font-mono font-semibold ${r.status === "cancelada" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                  {brl(r.total)}
                </span>
                {r.discount > 0 && r.status !== "cancelada" && (
                  <p className="text-xs text-red-500 font-mono">− {brl(r.discount)}</p>
                )}
              </>
            ),
          },
          {
            key: "acoes", header: "Ações", align: "right", className: "whitespace-nowrap",
            cell: (r) => (
              /* `stopPropagation` no contêiner: a linha inteira abre o detalhe,
                 e sem isto todo clique numa ação abriria o modal por baixo. */
              <span onClick={(e) => e.stopPropagation()} role="presentation" className="inline-flex items-center gap-0.5">
                {r.kind === "orcamento" && (
                  <>
                    <a href={r.wa_link} target="_blank" rel="noreferrer" data-testid="quote-whatsapp-btn"
                      aria-label={`Enviar orçamento ${r.number} no WhatsApp`} title="Enviar no WhatsApp (com link do PDF)"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"><MessageCircle size={16} /></a>
                    <a href={fileUrl(`/admin/quotes/${r.id}/pdf`)} target="_blank" rel="noreferrer" data-testid="quote-pdf-btn"
                      aria-label={`Abrir PDF do orçamento ${r.number}`} title="Abrir PDF"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-100"><FileText size={16} /></a>
                    <Button variant="ghost" size="iconSm" onClick={() => printQuote(r)} disabled={busy}
                      data-testid="quote-print-btn" aria-label={`Imprimir orçamento ${r.number}`} title="Imprimir no agente local"><Printer size={16} /></Button>
                    {r.status === "enviado" && (
                      <>
                        <Button variant="ghost" size="iconSm" onClick={() => setConverting(r)} disabled={busy}
                          data-testid="quote-convert-btn" aria-label={`Converter orçamento ${r.number} em venda`} title="Converter em venda"
                          className="hover:text-brand-600"><ShoppingCart size={16} /></Button>
                        <Button variant="ghost" size="iconSm" onClick={() => setPayingQuote(r)} disabled={busy}
                          data-testid="quote-paid-btn" aria-label={`Marcar orçamento ${r.number} como pago`} title="Marcar pago (sem gerar venda)"
                          className="hover:text-emerald-600"><CheckCircle2 size={16} /></Button>
                      </>
                    )}
                  </>
                )}
                {/* Ver não é poder mexer: a lista mostra o que está em aberto na
                    loja toda, mas retomar e descartar são do dono do carrinho —
                    ou do admin. Sem o `can_manage` os dois botões apareciam em
                    toda linha e só falhavam com 404 no clique. */}
                {r.kind === "aberta" && r.can_manage && (
                  <>
                    {/* Retomar acontece no PDV, onde a venda se fecha — aqui só
                        se leva a pessoa até lá, com o id no estado da rota. O
                        PDV é quem revalida preço e estoque (useHeldSales);
                        reconstruir o carrinho nesta tela duplicaria isso. */}
                    <Link to={panelLink("pdv")} state={{ resumeHeldId: r.id }} data-testid="held-open-pdv"
                      title="Retomar no PDV" aria-label="Retomar esta venda no PDV"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-100">
                      <ShoppingCart size={16} />
                    </Link>
                    <Button variant="ghost" size="iconSm" onClick={() => setDiscarding(r)}
                      data-testid="held-discard-btn" aria-label="Descartar venda em aberto" title="Descartar"
                      className="hover:text-red-600"><Trash2 size={16} /></Button>
                  </>
                )}
              </span>
            ),
          },
        ]}
      />

      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="registros" />

      {detail?.kind === "venda" && (
        <SaleDetail
          sale={detail}
          onClose={() => setDetail(null)}
          onCancel={() => { setCancelling(detail); setDetail(null); }}
          onChangeSeller={() => { setChangingSeller(detail); setDetail(null); }}
          onChangePayment={() => { setChangingPayment(detail); setDetail(null); }}
          onChangeCustomer={() => { setChangingCustomer(detail); setDetail(null); }}
          onEmitInvoice={() => { setEmitting(detail); setDetail(null); }}
        />
      )}
      {detail?.kind === "aberta" && (
        <HeldDetail held={detail} onClose={() => setDetail(null)}
          pdvPath={panelLink("pdv")}
          onDiscard={() => { setDiscarding(detail); setDetail(null); }} />
      )}
      {/* O orçamento não abria modal nenhum, e o clique na linha morria em
          silêncio. A tabela tem os atalhos (WhatsApp, PDF, imprimir), mas as
          duas decisões que importam quando o cliente responde — aprovar ou
          reabrir para negociar — não tinham onde acontecer. */}
      {detail?.kind === "orcamento" && (
        <QuoteDetail
          quote={detail}
          pdvPath={panelLink("pdv")}
          onClose={() => setDetail(null)}
          onConvert={() => { setConverting(detail); setDetail(null); }}
          onMarkPaid={() => { setPayingQuote(detail); setDetail(null); }}
          onResume={() => setDetail(null)}
        />
      )}

      {cancelling && (
        <CancelSaleDialog sale={cancelling} onClose={() => setCancelling(null)}
          onDone={() => { setCancelling(null); refetch(); }} />
      )}
      {changingSeller && (
        <ChangeSellerDialog sale={changingSeller} sellers={sellers} onClose={() => setChangingSeller(null)}
          onDone={() => { setChangingSeller(null); refetch(); }} />
      )}
      {changingPayment && (
        <ChangePaymentDialog sale={changingPayment} onClose={() => setChangingPayment(null)}
          onDone={() => { setChangingPayment(null); refetch(); }} />
      )}
      {changingCustomer && (
        <ChangeCustomerDialog sale={changingCustomer} onClose={() => setChangingCustomer(null)}
          onDone={() => { setChangingCustomer(null); refetch(); }} />
      )}
      {emitting && (
        <EmitInvoiceDialog sale={emitting} onClose={() => setEmitting(null)}
          onDone={() => { setEmitting(null); refetch(); }} />
      )}
      {converting && (
        <ConvertDialog quote={converting} onClose={() => setConverting(null)}
          onDone={() => { setConverting(null); refetch(); }} />
      )}

      <ConfirmDialog open={!!payingQuote} onClose={() => setPayingQuote(null)} onConfirm={() => markPaid(payingQuote)} danger={false}
        title="Marcar orçamento como pago"
        message={payingQuote ? `Confirmar recebimento do orçamento ${payingQuote.number} (${brl(payingQuote.total)})? O status muda para pago, mas nenhuma venda é gerada e o estoque não é baixado — para isso use "Converter em venda".` : ""}
        confirmLabel="Marcar pago" />

      <ConfirmDialog open={!!discarding} onClose={() => setDiscarding(null)} onConfirm={() => discardHeld(discarding)}
        title="Descartar venda em aberto"
        message={discarding ? `Descartar o carrinho "${discarding.label || "sem rótulo"}" de ${brl(discarding.total)}? Nada de estoque ou caixa é afetado — o carrinho nunca virou venda.` : ""}
        confirmLabel="Descartar" />

      <NewQuoteModal open={showQuoteForm} onClose={() => setShowQuoteForm(false)}
        onCreated={() => { setShowQuoteForm(false); refetch(); }} />
    </div>
  );
}
